<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
    <!-- 页面头部 -->
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">公告管理</h1>
      <button @click="handleCreate"
        class="primary-btn flex items-center gap-2">
        <PlusIcon class="w-5 h-5" />
        创建新公告
      </button>
    </div>

    <!-- 筛选器 -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-6">
      <div class="flex flex-wrap gap-4">
        <!-- 状态筛选 -->
        <div class="flex items-center gap-2">
          <label class="text-sm font-medium text-gray-700 dark:text-gray-300">状态:</label>
          <select v-model="filterForm.status" @change="handleFilter"
            class="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent">
            <option :value="undefined">全部</option>
            <option :value="AnnouncementStatus.DRAFT">📝 草稿</option>
            <option :value="AnnouncementStatus.PUBLISHED">✅ 已发布</option>
          </select>
        </div>

        <!-- 是否弹窗筛选 -->
        <div class="flex items-center gap-2">
          <label class="text-sm font-medium text-gray-700 dark:text-gray-300">显示方式:</label>
          <select v-model="filterForm.isPopup" @change="handleFilter"
            class="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent">
            <option :value="undefined">全部</option>
            <option :value="true">🔔 弹窗公告</option>
            <option :value="false">📋 普通公告</option>
          </select>
        </div>

        <!-- 优先级筛选 -->
        <div class="flex items-center gap-2">
          <label class="text-sm font-medium text-gray-700 dark:text-gray-300">优先级:</label>
          <select v-model="filterForm.priority" @change="handleFilter"
            class="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent">
            <option :value="undefined">全部</option>
            <option :value="AnnouncementPriority.HIGH">高优先级</option>
            <option :value="AnnouncementPriority.NORMAL">普通</option>
            <option :value="AnnouncementPriority.LOW">低优先级</option>
          </select>
        </div>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="flex justify-center items-center py-20">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
    </div>

    <!-- 公告列表 -->
    <div v-else-if="announcements.length > 0" class="space-y-4">
      <AnnouncementItem v-for="announcement in announcements" :key="announcement.id" :announcement="announcement"
        @view="handleView" @preview="handlePreview" @edit="handleEdit" @publish="handlePublish"
        @unpublish="handleUnpublish" @delete="handleDelete" />
    </div>

    <!-- 空状态 -->
    <div v-else class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center">
      <div class="text-gray-400 dark:text-gray-500 mb-4">
        <svg class="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
        </svg>
      </div>
      <p class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">暂无公告</p>
      <p class="text-sm text-gray-500 dark:text-gray-400">点击右上角"创建新公告"按钮创建第一条公告</p>
    </div>

    <!-- 分页 -->
    <Pagination v-if="pagination.total > 0"
      :total="pagination.total"
      v-model:current-page="pagination.page"
      v-model:page-size="pagination.limit"
      :show-jumper="false"
      @update:current-page="loadAnnouncements"
      @update:page-size="loadAnnouncements"
    />

    <!-- 删除确认模态框 -->
    <ConfirmModal ref="confirmModalRef" />

    <!-- 预览模态框 -->
    <AnnouncementPreviewModal ref="previewModalRef" :announcement="previewAnnouncement" @close="handlePreviewClose" />
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { PlusIcon } from '@heroicons/vue/24/outline'
import AnnouncementItem from '@/components/announcement/AnnouncementItem.vue'
import AnnouncementPreviewModal from '@/components/announcement/AnnouncementPreviewModal.vue'
import Pagination from '@/components/ui/Pagination.vue'
import { getAnnouncementsAPI, deleteAnnouncementAPI, publishAnnouncementAPI, unpublishAnnouncementAPI } from '@/api/announcement'
import { AnnouncementStatus, AnnouncementPriority, type Announcement } from '@/types/announcement'
import toaster from '@/utils/toaster'
import ConfirmModal from '@/components/ui/ConfirmModal.vue'

const router = useRouter()

// 状态
const loading = ref(false)
const announcements = ref<Announcement[]>([])
const pagination = reactive({
  page: 1,
  limit: 20,
  total: 0,
})
const confirmModalRef = ref<InstanceType<typeof ConfirmModal> | null>(null)
const previewModalRef = ref<InstanceType<typeof AnnouncementPreviewModal> | null>(null)
const previewAnnouncement = ref<Announcement | null>(null)

// 筛选表单
const filterForm = reactive({
  status: undefined as AnnouncementStatus | undefined,
  isPopup: undefined as boolean | undefined,
  priority: undefined as AnnouncementPriority | undefined,
})

/**
 * 加载公告列表
 */
const loadAnnouncements = async () => {
  loading.value = true
  try {
    const response = await getAnnouncementsAPI({
      page: pagination.page,
      limit: pagination.limit,
      status: filterForm.status,
      isPopup: filterForm.isPopup,
      priority: filterForm.priority,
    })

    if (response.data) {
      announcements.value = response.data.announcements
      pagination.total = response.data.total
    }
  } catch (error) {
    toaster.error('加载公告列表失败')
    console.error('加载公告列表失败:', error)
  } finally {
    loading.value = false
  }
}

/**
 * 处理筛选
 */
const handleFilter = () => {
  pagination.page = 1
  loadAnnouncements()
}

/**
 * 处理创建公告
 */
const handleCreate = () => {
  router.push('/announcement/create')
}

/**
 * 处理查看公告
 */
const handleView = (announcement: Announcement) => {
  router.push(`/announcement/${announcement.id}`)
}

/**
 * 处理预览公告
 */
const handlePreview = (announcement: Announcement) => {
  previewAnnouncement.value = announcement
  previewModalRef.value?.open()
}

/**
 * 处理预览关闭
 */
const handlePreviewClose = () => {
  previewAnnouncement.value = null
}

/**
 * 处理编辑公告
 */
const handleEdit = (announcement: Announcement) => {
  router.push(`/announcement/${announcement.id}/edit`)
}

/**
 * 处理发布公告
 */
const handlePublish = async (announcement: Announcement) => {
  if (!confirmModalRef.value) return

  try {
    const confirmed = await confirmModalRef.value.confirm(
      `确定要发布公告"${announcement.title}"吗？<br><br>发布后用户将能看到此公告`,
      '确认发布'
    )

    if (!confirmed) return

    await publishAnnouncementAPI(announcement.id)
    toaster.success('发布成功')
    loadAnnouncements()
  } catch (error) {
    toaster.error('发布失败')
    console.error('发布公告失败:', error)
  }
}

/**
 * 处理撤回公告
 */
const handleUnpublish = async (announcement: Announcement) => {
  if (!confirmModalRef.value) return

  try {
    const confirmed = await confirmModalRef.value.confirm(
      `确定要撤回公告"${announcement.title}"吗？<br><br>撤回后用户将无法看到此公告`,
      '确认撤回'
    )

    if (!confirmed) return

    await unpublishAnnouncementAPI(announcement.id)
    toaster.success('撤回成功')
    loadAnnouncements()
  } catch (error) {
    toaster.error('撤回失败')
    console.error('撤回公告失败:', error)
  }
}

/**
 * 处理删除公告
 */
const handleDelete = async (announcement: Announcement) => {
  if (!confirmModalRef.value) return

  try {
    const confirmed = await confirmModalRef.value.confirm(
      `确定要删除公告"${announcement.title}"吗？<br><br><span style="color: #ef4444;">此操作不可恢复</span>`,
      '确认删除'
    )

    if (!confirmed) return

    await deleteAnnouncementAPI(announcement.id)
    toaster.success('删除成功')
    loadAnnouncements()
  } catch (error) {
    toaster.error('删除失败')
    console.error('删除公告失败:', error)
  }
}

// 组件挂载时加载数据
onMounted(() => {
  loadAnnouncements()
})
</script>
